import { useEffect, useState } from "react";
import { RdvTypeValues, Transforms } from "cadendar-shared";
import EditRdv from "../components/EditRdv";
import { useAtomValue, useSetAtom } from "jotai";
import _ from "underts";
import {
  rdvToPostPoneDateAtom,
  selectedRdvIdAtom,
  usePreferences,
  useRdvPopupState,
  useSelectedWeek,
} from "../../main/state/globalState";
import useAllUsers from "../../users/hooks/useAllUsers";
import useUser from "../../users/hooks/useUser";
import useSelectedPatients from "../../patients/hooks/useSelectedPatients";
import { useThreeWeeksRdvs } from "../queries/rdvsQueries";
import useRdvProposal from "../../ficheAppel/hooks/useRdvProposal";
import { useHoraire } from "../../horaires/hooks/useHoraire";
import { trpc } from "../../main/components/trpc.ts";
import { skipToken } from "@tanstack/react-query";
import { AppointmentsDTO } from "cadendar-trpc-server";

export type EditRdvState = AppointmentsDTO & {
  reminderChecked: boolean;
  confirmChecked: boolean;
};

const getInitialState = (
  rdv: AppointmentsDTO,
  reminderState: { reminders: any[]; confirmationSms: any[] }
): EditRdvState => {
  return {
    ...rdv,
    reminderChecked: reminderState.reminders.length > 0,
    confirmChecked: reminderState.confirmationSms.length > 0,
  };
};

const useEditRdv = (rdvState: AppointmentsDTO | null) => {
  const [tempRdv, setTempRdv] = useState<
    | null
    | (AppointmentsDTO & { reminderChecked: boolean; confirmChecked: boolean })
  >(null);

  const remindersDTO = trpc.reminder.getRemindersByAptId.useQuery(
    rdvState?.id || skipToken
  ).data;

  useEffect(() => {
    if (!rdvState || !remindersDTO) return;
    setTempRdv(getInitialState(rdvState, remindersDTO));
  }, [JSON.stringify({ ...rdvState, remindersDTO })]);
  const horaire = useHoraire(tempRdv && tempRdv.date);
  const { selectedWeek } = useSelectedWeek();
  const [allRdvs] = useThreeWeeksRdvs(selectedWeek);
  const prefs = usePreferences();
  const onSetHM = ({ hour, minute }: { hour: number; minute: number }) => {
    setTempRdv((tempRdv) => {
      if (!tempRdv) return null;
      const dureesPossibles =
        horaire &&
        prefs &&
        tempRdv &&
        Transforms.rdvs.getDurees2(
          horaire,
          allRdvs,
          tempRdv.date,
          { hour, minute },
          prefs.duree_min_rdv,
          tempRdv
        );
      const choosenDuree =
        (dureesPossibles &&
          (dureesPossibles.includes(tempRdv.duration)
            ? tempRdv.duration
            : dureesPossibles[0])) ||
        0;
      return { ...tempRdv, hour, minute, duration: choosenDuree };
    });
  };

  const onSetDuree = (duration: number) => {
    setTempRdv((tempRdv) => (tempRdv ? { ...tempRdv, duration } : null));
  };
  const onSetType = (type: RdvTypeValues) => {
    setTempRdv((tempRdv) => (tempRdv ? { ...tempRdv, type } : null));
  };
  const onSetObjet = (object: string) => {
    setTempRdv((tempRdv) => (tempRdv ? { ...tempRdv, object } : null));
  };
  const handleToggleReminder = <
    T extends { reminderChecked: boolean; confirmChecked: boolean },
  >(
    tempRdv: T
  ) => {
    if (tempRdv.reminderChecked) {
      return {
        ...tempRdv,
        reminderChecked: !tempRdv.reminderChecked,
        confirmChecked: false,
      };
    }
    return {
      ...tempRdv,
      reminderChecked: true,
    };
  };
  const handleToggleConfirm = <
    T extends { reminderChecked: boolean; confirmChecked: boolean },
  >(
    tempRdv: T
  ) => {
    if (!tempRdv.reminderChecked && !tempRdv.confirmChecked) {
      return {
        ...tempRdv,
        confirmChecked: true,
        reminderChecked: true,
      };
    }
    return {
      ...tempRdv,
      confirmChecked: !tempRdv.confirmChecked,
    };
  };

  const onToggleReminder = () => {
    setTempRdv((tempRdv) => (tempRdv ? handleToggleReminder(tempRdv) : null));
  };
  const onToggleConfirm = () => {
    setTempRdv((tempRdv) => (tempRdv ? handleToggleConfirm(tempRdv) : null));
  };
  return {
    tempRdv,
    onSetHM,
    onSetDuree,
    onSetType,
    onSetObjet,
    onToggleReminder,
    onToggleConfirm,
  };
};

interface EditRdvContainerProps {
  onOpenFicheAppel: () => void;
}

const EditRdvContainer = (props: EditRdvContainerProps) => {
  const rdvId = useAtomValue(selectedRdvIdAtom);
  const patients = useSelectedPatients();
  const patient = patients ? patients[0] : null;
  const { onCloseRdvPopup } = useRdvPopupState();
  const { data: oldRdv } = trpc.rdv.getById.useQuery(rdvId || skipToken);
  const remindersDTO = trpc.reminder.getRemindersByAptId.useQuery(
    oldRdv?.id || skipToken
  ).data;
  const {
    tempRdv,
    onSetHM,
    onSetDuree,
    onSetType,
    onSetObjet,
    onToggleReminder,
    onToggleConfirm,
  } = useEditRdv(oldRdv || null);
  const horaire = useHoraire(tempRdv && tempRdv.date);
  const { selectedWeek } = useSelectedWeek();
  const [allRdvs] = useThreeWeeksRdvs(selectedWeek);
  const prefs = usePreferences();
  const hmPossibles =
    horaire &&
    prefs &&
    oldRdv &&
    Transforms.rdvs.heuresPossibles3(
      horaire,
      allRdvs,
      oldRdv.date,
      prefs.duree_min_rdv,
      oldRdv
    );
  const dureesPossibles =
    horaire &&
    prefs &&
    oldRdv &&
    tempRdv &&
    Transforms.rdvs.getDurees2(
      horaire,
      allRdvs,
      oldRdv.date,
      { hour: tempRdv.hour, minute: tempRdv.minute },
      prefs.duree_min_rdv,
      oldRdv
    );
  const remindersCheckboxEnabled =
    patient && Transforms.patients.hasRemindersTargets(patient);
  const confirmCheckboxEnabled =
    patient && Transforms.patients.hasConfirmationTarget(patient);
  const allUsers = useAllUsers();
  const onCancelRdvEdit = () => {
    onCloseRdvPopup();
  };
  // const onSave = useConfiguratedCommiter(saveEditedRdv);
  const saveMutation = trpc.rdv.commitEdited.useMutation({
    onSuccess: () => {
      onCloseRdvPopup();
      trpcUtils.rdv.invalidate();
    },
  });
  const trpcUtils = trpc.useContext();
  const cancelWithoutLoggingMutation =
    trpc.rdv.deleteWithoutLogging.useMutation({
      onSuccess: () => {
        onCloseRdvPopup();
        trpcUtils.rdv.invalidate();
      },
    });

  const cancelWithLoggingMutation = trpc.rdv.cancelWithLogging.useMutation({
    onSuccess: () => {
      onCloseRdvPopup();
      trpcUtils.rdv.invalidate();
    },
  });
  const autoDeleteMutation = trpc.rdv.autoDelete.useMutation({
    onSuccess: () => {
      onCloseRdvPopup();
      trpcUtils.rdv.invalidate();
    },
  });

  const handleSave = async () => {
    if (!tempRdv || !oldRdv) return;
    const addReminders =
      tempRdv.reminderChecked &&
      !!remindersDTO &&
      remindersDTO.reminders.length === 0;
    const addConfirmations =
      tempRdv.confirmChecked &&
      !!remindersDTO &&
      remindersDTO.confirmationSms.length === 0;
    const removeReminders =
      !tempRdv.reminderChecked &&
      !!remindersDTO?.reminders &&
      remindersDTO.reminders.length > 0;
    const removeConfirmations =
      !tempRdv.confirmChecked &&
      !!remindersDTO?.confirmationSms &&
      remindersDTO.confirmationSms.length > 0;
    saveMutation.mutate({
      rdv: tempRdv,
      addReminders,
      addConfirmations,
      removeReminders,
      removeConfirmations,
    });
  };
  const handleCancelWithoutLogging = async () => {
    if (!oldRdv) return;
    cancelWithoutLoggingMutation.mutate({ _id: oldRdv.id });
  };

  const handleCancelWithLogging = async () => {
    if (!oldRdv) return;
    cancelWithLoggingMutation.mutate({ _id: oldRdv.id });
  };

  const handleAutoDelete = async () => {
    if (!oldRdv) return;
    autoDeleteMutation.mutate({ id: oldRdv.id, mode: "manuel" });
  };

  const { setDuree, assignMotifsNums, setType, setObjet } = useRdvProposal();
  const user = useUser();
  const isSecretary = user?.tag === "secretary";
  const setPostponedDate = useSetAtom(rdvToPostPoneDateAtom);
  const handlePostPone = async () => {
    if (!oldRdv) return;
    setPostponedDate(oldRdv.date);
    setDuree(oldRdv.duration);
    setType(oldRdv.type as RdvTypeValues);
    oldRdv.object && setObjet(oldRdv.object);
    assignMotifsNums(oldRdv.motif || []); //hack pour forcer strategor à runner
    if (isSecretary) {
      try {
        await autoDeleteMutation.mutateAsync({
          id: oldRdv.id,
          mode: "manuel",
        });
      } catch {
        return;
      }
    } else {
      cancelWithLoggingMutation.mutate({ _id: oldRdv.id });
    }
    props.onOpenFicheAppel();
  };
  if (tempRdv && hmPossibles && dureesPossibles) {
    return (
      <EditRdv
        rdvCaracsState={tempRdv}
        hmPossibles={hmPossibles}
        dureesPossibles={dureesPossibles}
        remindersCheckBoxEnabled={!!remindersCheckboxEnabled}
        confirmCheckBoxEnabled={!!confirmCheckboxEnabled}
        onSaveRdv={handleSave}
        onCancelRdvEdit={onCancelRdvEdit}
        onDeleteWithoutLogging={handleCancelWithoutLogging}
        onCancelWithLogging={handleCancelWithLogging}
        onAutoDelete={handleAutoDelete}
        onPostPone={handlePostPone}
        isSecretary={isSecretary}
        rdvCaracsCallbacks={{
          onDureeChange: onSetDuree,
          onTypeChange: onSetType,
          onObjetChange: onSetObjet,
          onReminderClick: onToggleReminder,
          onConfirmClick: onToggleConfirm,
          onHMChange: onSetHM,
        }}
        users={allUsers}
        rdv={oldRdv}
        patient={patient}
      />
    );
  } else {
    return <></>;
  }
};

export default EditRdvContainer;
